import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "./AuthContext";
import { getMenuPrefs, saveMenuPrefs } from "../services/uiPrefsApi";
import { EMPTY_MENU_PREFS, normalizeMenuPrefs } from "../config/navConfig";

/**
 * Personal menu settings (правка №5, №52). [Claude] 2026-11-02 / 2026-11-03
 * Source of truth is the server (settings follow the account). A per-user copy
 * in the browser only avoids the menu jumping while the server answers.
 * `loaded` turns true once the server answered (or failed / timed out), so the
 * start page is chosen from fresh settings.
 * `save(next)` — сохранить целиком (экран «Настройка меню»);
 * `update(patch)` — мелкое изменение сразу на экране и тихо на сервер через полсекунды
 * (раскрытые группы в меню и т. п.).
 */

const CACHE_PREFIX = "hr-menu-prefs:v1";
const LOAD_TIMEOUT_MS = 3000;
const UPDATE_DELAY_MS = 500;

function readCache(uid) {
    if (!uid || typeof window === "undefined") return null;
    try {
        const raw = window.localStorage.getItem(`${CACHE_PREFIX}:${uid}`);
        return raw ? normalizeMenuPrefs(JSON.parse(raw)) : null;
    } catch {
        return null;
    }
}

function writeCache(uid, prefs) {
    if (!uid || typeof window === "undefined") return;
    try {
        window.localStorage.setItem(`${CACHE_PREFIX}:${uid}`, JSON.stringify(prefs));
    } catch {
        // Cache is optional.
    }
}

const MenuPrefsContext = createContext({
    prefs: EMPTY_MENU_PREFS,
    loaded: true,
    save: async (next) => normalizeMenuPrefs(next),
    update: () => {},
});

export function MenuPrefsProvider({ children }) {
    const auth = useAuth() || {};
    const authLoading = Boolean(auth.loading);
    const uid = auth.user ? String(auth.user.erp_user_id || auth.user.id || "") : "";
    const [state, setState] = useState(() => ({ uid, prefs: readCache(uid) || EMPTY_MENU_PREFS, loaded: false }));
    const pending = useRef(null);
    const timer = useRef(null);

    useEffect(() => {
        if (authLoading) return undefined;
        if (!uid) {
            setState({ uid: "", prefs: EMPTY_MENU_PREFS, loaded: true });
            return undefined;
        }
        let cancelled = false;
        setState({ uid, prefs: readCache(uid) || EMPTY_MENU_PREFS, loaded: false });
        const loadTimer = setTimeout(() => {
            if (!cancelled) setState((s) => (s.uid === uid ? { ...s, loaded: true } : s));
        }, LOAD_TIMEOUT_MS);
        getMenuPrefs()
            .then((res) => {
                if (cancelled) return;
                const prefs = normalizeMenuPrefs(res);
                writeCache(uid, prefs);
                setState({ uid, prefs, loaded: true });
            })
            .catch(() => {
                if (!cancelled) setState((s) => (s.uid === uid ? { ...s, loaded: true } : s));
            })
            .finally(() => clearTimeout(loadTimer));
        return () => {
            cancelled = true;
            clearTimeout(loadTimer);
        };
    }, [uid, authLoading]);

    useEffect(() => () => clearTimeout(timer.current), []);

    const save = useCallback(async (next) => {
        clearTimeout(timer.current);
        pending.current = null;
        const saved = normalizeMenuPrefs(await saveMenuPrefs(normalizeMenuPrefs(next)));
        writeCache(uid, saved);
        setState({ uid, prefs: saved, loaded: true });
        return saved;
    }, [uid]);

    const update = useCallback((patch) => {
        setState((s) => {
            const prefs = normalizeMenuPrefs({ ...s.prefs, ...patch });
            writeCache(uid, prefs);
            pending.current = prefs;
            return { ...s, prefs };
        });
        clearTimeout(timer.current);
        timer.current = setTimeout(() => {
            const next = pending.current;
            pending.current = null;
            if (next && uid) Promise.resolve(saveMenuPrefs(next)).catch(() => {});
        }, UPDATE_DELAY_MS);
    }, [uid]);

    const value = useMemo(() => {
        const current = state.uid === uid;
        return {
            prefs: current ? state.prefs : (readCache(uid) || EMPTY_MENU_PREFS),
            loaded: current && state.loaded,
            save,
            update,
        };
    }, [state, uid, save, update]);

    return <MenuPrefsContext.Provider value={value}>{children}</MenuPrefsContext.Provider>;
}

export function useMenuPrefs() {
    return useContext(MenuPrefsContext);
}
